// SEO COLLECTION PAGES
//
// People don't search "Daikin price list". They search "1.5 ton daikin ac",
// "3 star 1.5 ton ac price", "daikin inverter ac 1 ton". Each of those wants
// its own landing page with its own H1 and its own copy.
//
// Each entry generates a real page at /{slug}. Routes are declared explicitly
// in App.js, so they take priority over the /:slug LocationPage catch-all.
//
// The `intro` on each page is genuinely different, not a template with the
// tonnage swapped. Near-identical pages get treated as duplicates and none of
// them rank — which is the mistake to avoid, not a cosmetic preference.
//
// Add a collection by adding an object here. Nothing else needs touching.

export const COLLECTIONS = [
  {
    slug: 'daikin-1-ton-ac-price',
    filter: { soldAs: '1.0' },
    h1: 'Daikin 1 Ton AC Price List',
    title: 'Daikin 1 Ton AC Price in Delhi NCR 2026 | Authorised Dealer',
    meta: 'Daikin 1 ton split AC prices in Delhi, Noida, Gurgaon. 2, 3 and 5 star inverter models with installation cost shown. Cooling Delhi since 1990. Authorised Daikin dealer since 2003.',
    intro:
      'A 1 ton unit suits a bedroom up to roughly 110 sq ft on a middle floor. On a top floor or a west-facing room the same area usually needs 1.5 ton, because roof and afternoon sun add more load than floor area alone suggests. Every model below is rated between 3.35 and 3.52 kW — the label says 1 ton, the actual capacity varies, and it is the kW figure that decides whether the room holds temperature.',
  },
  {
    slug: 'daikin-1-5-ton-ac-price',
    filter: { soldAs: '1.5' },
    h1: 'Daikin 1.5 Ton AC Price List',
    title: 'Daikin 1.5 Ton AC Price in Delhi NCR 2026 | Authorised Dealer',
    meta: 'Daikin 1.5 ton split AC prices in Delhi, Noida, Gurgaon, Faridabad. 2, 3 and 5 star inverter models, installation cost included. Authorised Daikin dealer.',
    intro:
      'The most common size sold in Delhi NCR, and the one worth reading the specification on. Units marketed as 1.5 ton range from 5.0 to 5.28 kW here — that is 1.42 to 1.50 TR, a real difference of about 6%. JTKJ53 at 5.28 kW is the only true 1.5 ton in the range; the rest sit slightly under. For a standard bedroom or a small living room that gap rarely matters, but on a top floor with west sun it does.',
  },
  {
    slug: 'daikin-1-8-ton-ac-price',
    filter: { soldAs: '1.8' },
    h1: 'Daikin 1.8 Ton AC Price List',
    title: 'Daikin 1.8 Ton AC Price in Delhi NCR 2026 | Authorised Dealer',
    meta: 'Daikin 1.8 ton split AC prices in Delhi NCR. 3 and 5 star inverter models with installation. Authorised Daikin dealer, 35 years in South Extension.',
    intro:
      'Rated at 6.0 kW, which is 1.71 TR rather than 1.8. This is the size for a large bedroom, a living room around 180 to 200 sq ft, or a smaller room that runs hot because of an exposed roof. It is also the point where the jump to a 5 star unit starts costing meaningfully more upfront, so it is worth looking at the running cost figures on each product page before deciding.',
  },
  {
    slug: 'daikin-2-ton-ac-price',
    filter: { soldAs: '2.0' },
    h1: 'Daikin 2 Ton AC Price List',
    title: 'Daikin 2 Ton AC Price in Delhi NCR 2026 | Authorised Dealer',
    meta: 'Daikin 2 ton split AC price in Delhi, Noida, Gurgaon. Inverter split AC with installation cost shown openly. Cooling Delhi since 1990. Authorised Daikin dealer since 2003.',
    intro:
      'At 7.03 kW this is a genuine 2 TR unit, one of the few sizes where the marketing label and the actual capacity agree. Suited to open-plan living rooms, small shops and offices up to roughly 240 sq ft. Above this, a single split usually stops being the right answer and a cassette or two smaller units make more sense — worth a conversation rather than an online order.',
  },
  {
    slug: 'daikin-inverter-ac-price',
    filter: { inverter: true },
    h1: 'Daikin Inverter AC Price List',
    title: 'Daikin Inverter AC Price in Delhi NCR 2026 | Authorised Dealer',
    meta: 'Daikin inverter split AC prices in Delhi NCR, 1 ton to 2.6 ton. 3 and 5 star models with ISEER ratings and installation cost. Authorised Daikin dealer.',
    intro:
      'An inverter compressor varies its speed instead of switching on and off, which cuts running cost and keeps the room temperature steadier. Every model here carries a 10 year compressor warranty against 5 on fixed speed. Whether the higher price pays back depends entirely on hours of use — a bedroom running four hours a night takes far longer to recover the premium than a living room running ten.',
  },
  {
    slug: 'daikin-5-star-ac-price',
    filter: { stars: 5 },
    h1: 'Daikin 5 Star AC Price List',
    title: 'Daikin 5 Star AC Price in Delhi NCR 2026 | ISEER 5.70 | Citi Cool',
    meta: 'Daikin 5 star inverter AC prices in Delhi NCR. ISEER 5.70 models from 1 ton to 1.8 ton with running cost figures. Authorised Daikin dealer.',
    intro:
      'Every 5 star model here runs at ISEER 5.70, the top of Daikin\u2019s residential range. Be realistic about the payback: against the equivalent 3 star unit the premium takes roughly five to nine years to recover at 1,600 hours a year on a Delhi tariff. If you run the AC hard through a long summer that is a real return. If you use it lightly, buy the 5 star for the build quality and the filtration rather than the electricity bill.',
  },
  {
    slug: 'daikin-3-star-ac-price',
    filter: { stars: 3 },
    h1: 'Daikin 3 Star AC Price List',
    title: 'Daikin 3 Star AC Price in Delhi NCR 2026 | Authorised Dealer',
    meta: 'Daikin 3 star inverter AC prices in Delhi, Noida, Gurgaon. ISEER 4.40 models from 1 ton to 2.6 ton with installation cost. Authorised Daikin dealer.',
    intro:
      'ISEER 4.40 across the range, and for most households the sensible buy. The 3 star inverter models carry the same 10 year compressor warranty as the 5 star ones and cool just as effectively — the difference is electricity consumed, not performance. On typical residential usage the gap works out to a few hundred rupees a month at most.',
  },
  {
    slug: 'daikin-1-5-ton-3-star-ac-price',
    filter: { soldAs: '1.5', stars: 3 },
    h1: 'Daikin 1.5 Ton 3 Star AC Price',
    title: 'Daikin 1.5 Ton 3 Star AC Price in Delhi NCR 2026 | Citi Cool',
    meta: 'Daikin 1.5 ton 3 star inverter split AC price in Delhi NCR. GTKL, GTKC and FTHT models with installation cost shown. Authorised Daikin dealer.',
    intro:
      'The single most searched configuration in Delhi NCR, and the sweet spot on price. Three options sit here: GTKC as the value pick with no special filtration, GTKL adding PM2.5 and active carbon filters, and FTHT which heats as well as cools — useful for a north-facing room in a Delhi January. All three are ISEER 4.40 and 5.0 kW, so they cool identically; you are choosing on filtration and features, not performance.',
  },
  {
    slug: 'daikin-1-5-ton-5-star-ac-price',
    filter: { soldAs: '1.5', stars: 5 },
    h1: 'Daikin 1.5 Ton 5 Star AC Price',
    title: 'Daikin 1.5 Ton 5 Star AC Price in Delhi NCR 2026 | ISEER 5.70',
    meta: 'Daikin 1.5 ton 5 star inverter AC price in Delhi NCR. GTKM and JTKJ Waizu models, ISEER 5.70, with installation. Authorised Daikin dealer.',
    intro:
      'Two options at this rating. GTKM is the straightforward 5 star premium unit with PM2.5 and active carbon filtration, rated to cool at 55\u00B0C ambient. JTKJ Waizu adds WiFi, voice control, Daikin\u2019s Streamer purification and Intelligent Eye, and is also the only genuine 1.5 TR unit in the range at 5.28 kW rather than 5.0. Both consume the same electricity; the difference is capacity and features.',
  },
  {
    slug: 'daikin-1-ton-3-star-ac-price',
    filter: { soldAs: '1.0', stars: 3 },
    h1: 'Daikin 1 Ton 3 Star AC Price',
    title: 'Daikin 1 Ton 3 Star AC Price in Delhi NCR 2026 | Citi Cool',
    meta: 'Daikin 1 ton 3 star inverter split AC price in Delhi, Noida, Gurgaon. GTKC and FTHT models with installation cost. Authorised Daikin dealer.',
    intro:
      'The standard bedroom choice, and the lowest priced inverter in the Daikin range. Both models here are ISEER 4.40 at 3.52 kW. Worth checking your room before ordering: a 1 ton unit on a top floor with afternoon sun will run continuously and never quite hold temperature, and the fix is a larger unit rather than a colder setting.',
  },
  {
    slug: 'daikin-hot-and-cold-ac-price',
    filter: { series: 'FTHT' },
    h1: 'Daikin Hot and Cold AC Price List',
    title: 'Daikin Hot and Cold AC Price in Delhi NCR 2026 | FTHT Series',
    meta: 'Daikin FTHT hot and cold inverter AC prices in Delhi NCR. Heats and cools, 1 ton to 1.8 ton, with installation cost. Authorised Daikin dealer.',
    intro:
      'The FTHT series runs as a heat pump in winter, delivering warm air down to -10\u00B0C outdoors. In a Delhi flat that replaces a room heater entirely and costs far less to run, because a heat pump moves heat rather than generating it. Also carries PM1.0 and CO2 filtration. These ship with the piping kit only — there is no without-kit variant in this series.',
  },
  {
    slug: 'daikin-fixed-speed-ac-price',
    filter: { inverter: false },
    h1: 'Daikin Fixed Speed AC Price List',
    title: 'Daikin Fixed Speed AC Price in Delhi NCR 2026 | Non-Inverter',
    meta: 'Daikin fixed speed non-inverter AC prices in Delhi NCR. GTQ series 0.8 to 1.5 ton, lowest upfront cost. Cooling Delhi since 1990. Authorised Daikin dealer since 2003.',
    intro:
      'The GTQ series is the lowest entry price in the Daikin range, and the honest answer for rooms used occasionally — a guest bedroom, a second office, a shop that opens seasonally. Fixed speed means the compressor is either on or off, so running cost is higher and the temperature swings more. Compressor warranty is 5 years rather than 10. If the unit will run daily through summer, the inverter models pay for themselves.',
  },
];

export const findCollection = (slug) => COLLECTIONS.find((c) => c.slug === slug) || null;

export function matchesCollection(model, filter) {
  return Object.entries(filter).every(([k, v]) => model[k] === v);
}